import { supabase } from './supabase'
import type { TaggedPerson } from './photoTags'
import { readExif } from './exif'
import { isWhole, moveSpot, pixelBox, WHOLE, type CropShapeKey, type NormRect } from './photoCrop'

/**
 * Photo upload for the composer (part of FND-6).
 *
 * ── THE RESIZE IS NOT AN OPTIMISATION ───────────────────────────────────────
 *
 * A photo straight off a current phone is 8–12 MB and 4000px wide. The feed
 * renders it at 700px. Uploading the original costs the crew member their own
 * upstream — on the tethered connection this hub is built for, an 8 MB upload
 * on a bad signal is minutes, and they will conclude the hub is broken and stop
 * posting. It then costs every reader the download.
 *
 * So the client re-encodes to 2000px on the long edge before it uploads. That
 * is generous for a feed card, comfortable for a moderator judging PPE in a
 * photo, and lands around 300 KB–1 MB.
 *
 * ── AND THE RE-ENCODE IS WHAT STRIPS THE EXIF ───────────────────────────────
 *
 * This is the part that matters more than the file size. A site photograph off
 * a phone carries GPS coordinates, the device, and the exact timestamp. ConeTec
 * works on other people's sites, so publishing the coordinates of a client's
 * site to ~900 people is a confidentiality problem that has nothing to do with
 * what the photo shows — and guideline 4 already forbids "site data covered by
 * an NDA".
 *
 * Drawing to a canvas and calling toBlob() produces a fresh file with no EXIF
 * at all. It is a side effect of the resize rather than a separate step, which
 * is why the two live in one function: someone removing the resize for quality
 * reasons would silently remove the strip too, so they are deliberately
 * inseparable.
 *
 * IT IS STILL CLIENT-SIDE, AND THEREFORE A COURTESY RATHER THAN A CONTROL.
 * Anyone can call the Storage API directly with an untouched file. That is why
 * content_attachment.exif_stripped is named for what the CLIENT CLAIMS, and why
 * a server-side strip is a follow-up rather than something to pretend is done.
 *
 * ── ORIENTATION ─────────────────────────────────────────────────────────────
 *
 * Stripping EXIF also strips the Orientation tag, which is how a phone records
 * "this was shot rotated" without rotating the pixels. So a naive canvas
 * re-encode turns every portrait photo sideways. createImageBitmap with
 * imageOrientation:'from-image' applies the rotation to the pixels first, which
 * is the only reason the output can safely have no orientation tag.
 */

/** 2000px on the long edge. See the header — a feed card renders at ~700. */
const MAX_EDGE = 2000
/** 0.82 JPEG. Above ~0.85 the file grows fast for no visible gain on a photo. */
const QUALITY = 0.82
/** Nine, matching the schema constraint. Past that it is an album. */
export const MAX_PHOTOS = 9

export type PreparedPhoto = {
  /** Object URL for the composer preview. Revoke it when the composer closes. */
  previewUrl: string
  file: Blob
  fileName: string
  width: number
  height: number
  /** Filled in after upload. */
  storagePath?: string
  altText: string
  /** 148 / 150 (FED-53). What THIS photo may be used for: the photo contest,
   *  and the use licence (marketing, sales, internal training and other uses
   *  — what earns the 50 stars, David 10 Oct). Posting is the opt-in: both
   *  are on until the poster opts the photo out in the opt-out dialog. */
  uses: PhotoUses
  /** 148. The poster's optional reason for an opt-out (<= 280). */
  reason: string
  /** 149 / 150. What this photo sends to photo room admins: the camera and
   *  date taken, and (separately, David 10 Oct) where it was taken. Both on
   *  by default, every photo; turned off in the same opt-out dialog. */
  details: PhotoDetails
  /** 143 (FED-49). People tagged in THIS photo, saved after the post saves;
   *  161: each with where they are in it, or null if not placed. */
  tagged: TaggedPerson[]
  /** 146. The file as picked, kept only so a TICKED photo can also go up at
   *  full size (keepOriginal). Never uploaded as-is: it still carries GPS.
   *  FED-66: also what a crop is cut from, so a crop loses no resolution. */
  source?: Blob
  /** FED-66. The UNCROPPED 2,000 px copy — what the crop dialog shows, so
   *  re-cropping starts from the whole photo and not from the last crop.
   *  Never uploaded. Absent on a photo that was never cropped (then `file`
   *  is the whole photo). */
  whole?: Blob
  /** FED-66. The crop, as fractions of the upright photo, applied to BOTH
   *  the feed copy and the full-size copy (lib/photoCrop.ts says why).
   *  Null = the whole photo. */
  crop?: NormRect | null
  /** FED-66. Which shape was chosen, so the dialog reopens on it. */
  cropShape?: CropShapeKey
}

/** 150 (David, 10 Oct). Two things a photo can be opted out of: the photo
 *  contest (costs nothing), and the use licence — marketing, sales, internal
 *  training and other uses — which is what earns the 50 stars. The server
 *  keeps three licence columns (148); the client writes them as one. */
export type PhotoUses = { contest: boolean; licence: boolean }
export type PhotoUseKey = keyof PhotoUses
export const USE_KEYS: PhotoUseKey[] = ['contest', 'licence']
export const ALL_USES: PhotoUses = { contest: true, licence: true }
export const NO_USES: PhotoUses = { contest: false, licence: false }

/** 150. The two kinds of photo metadata, opted out separately. */
export type PhotoDetails = { camera: boolean; location: boolean }
export type DetailKey = keyof PhotoDetails
export const DETAIL_KEYS: DetailKey[] = ['camera', 'location']
export const ALL_DETAILS: PhotoDetails = { camera: true, location: true }

/** Licensed — what the room, the full-size copy and the location are for. */
export function usedOutsideContests(u: PhotoUses): boolean {
  return u.licence
}

/** The uses this photo is opted out of, in the dialog's order. */
export function optedOutOf(u: PhotoUses): PhotoUseKey[] {
  return USE_KEYS.filter((k) => !u[k])
}

/** What create_post's p_attachments expects, per element. */
export type AttachmentInput = {
  storage_path: string
  mime_type: string
  byte_size: number
  width: number
  height: number
  alt_text: string | null
  exif_stripped: boolean
}

/**
 * Resize, re-orient and re-encode one picked file.
 *
 * Rejects rather than silently passing the original through when anything goes
 * wrong: an original that reaches Storage still carries the GPS coordinates the
 * whole function exists to remove, so a silent fallback would be the one
 * failure mode worth avoiding.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    throw new Error(`${file.name} is not a JPEG, PNG or WebP.`)
  }

  const { blob, width, height } = await encodeRegion(file, null, { maxEdge: MAX_EDGE, quality: QUALITY }).catch(() => {
    throw new Error(`${file.name} could not be processed.`)
  })

  return {
    previewUrl: URL.createObjectURL(blob),
    file: blob,
    // Always .jpg: the output IS a JPEG regardless of what went in, and a .png
    // name on JPEG bytes is the kind of mismatch that breaks a content-type
    // check three months later.
    fileName: file.name.replace(/\.[^.]+$/, '') + '.jpg',
    width,
    height,
    altText: '',
    uses: { ...ALL_USES },
    reason: '',
    details: { ...ALL_DETAILS },
    tagged: [],
    source: file,
  }
}

/**
 * The one re-encode (FED-66 gave it a home; the reasons are 019's, in this
 * file's header). Upright first (from-image), then the region if any, then
 * scaled down to fit, drawn to a canvas and written as a fresh JPEG — which
 * is what strips the EXIF. Every copy of a feed photo comes through here: the
 * feed copy, a cropped feed copy, and the full-size copy.
 *
 * `maxEdge` caps the long side; `maxPixels` caps the area (the full-size
 * copy, for iOS Safari's canvas limit). Never scales up.
 */
async function encodeRegion(
  file: Blob,
  region: NormRect | null,
  opts: { maxEdge?: number; maxPixels?: number; quality: number },
): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const box = region && !isWhole(region)
      ? pixelBox(region, bitmap.width, bitmap.height)
      : { sx: 0, sy: 0, sw: bitmap.width, sh: bitmap.height }
    let scale = 1
    if (opts.maxEdge) scale = Math.min(scale, opts.maxEdge / Math.max(box.sw, box.sh))
    if (opts.maxPixels) scale = Math.min(scale, Math.sqrt(opts.maxPixels / (box.sw * box.sh)))
    const width = Math.max(1, Math.floor(box.sw * scale))
    const height = Math.max(1, Math.floor(box.sh * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('This browser cannot process images.')
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, box.sx, box.sy, box.sw, box.sh, 0, 0, width, height)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', opts.quality))
    if (!blob) throw new Error('This photo could not be processed.')
    return { blob, width, height }
  } finally {
    bitmap.close?.()
  }
}

/**
 * FED-66. Crop a prepared (not yet uploaded) photo. The feed copy is cut
 * again from the PICKED file, so zooming in costs no resolution; the
 * full-size copy is cut from the same fractions later, in keepOriginal.
 *
 * Tags placed before the crop move with it (moveSpot); one that falls outside
 * stays tagged and loses its pin. `crop` null = back to the whole photo.
 *
 * Returns a new photo with its own new previewUrl; the CALLER revokes the
 * old one (the whole copy is kept as a Blob, not as that URL).
 */
export async function cropPhoto(
  photo: PreparedPhoto,
  crop: NormRect | null,
  shape: CropShapeKey,
): Promise<PreparedPhoto> {
  if (!photo.source) throw new Error('This photo cannot be cropped.')
  const next = isWhole(crop) ? null : crop
  const from = photo.crop ?? WHOLE
  const to = next ?? WHOLE
  const whole = photo.whole ?? photo.file
  const out = next
    ? await encodeRegion(photo.source, next, { maxEdge: MAX_EDGE, quality: QUALITY })
    : await sizeOf(whole)
  return {
    ...photo,
    previewUrl: URL.createObjectURL(out.blob),
    file: out.blob,
    width: out.width,
    height: out.height,
    whole,
    crop: next,
    cropShape: shape,
    tagged: photo.tagged.map((p) => ({ ...p, ...moveSpot(p.x, p.y, from, to) })),
  }
}

/** The whole copy is already encoded; only its size is needed back. */
async function sizeOf(blob: Blob): Promise<{ blob: Blob; width: number; height: number }> {
  const b = await createImageBitmap(blob)
  const r = { blob, width: b.width, height: b.height }
  b.close?.()
  return r
}

/**
 * Upload one prepared photo and return the attachment row create_post wants.
 *
 * ── THE PATH SHAPE IS LOAD-BEARING ──────────────────────────────────────────
 *
 * `<tenant_id>/<person_id>/<uuid>.jpg`. The Storage RLS policies in migration
 * 019 read the first segment as the tenant and the second as the uploader, and
 * refuse an insert where the second is not you. Changing this shape breaks the
 * policies silently — uploads start failing with a permission error that looks
 * like a session problem.
 *
 * The path is generated BEFORE the post exists, which is why it is not scoped
 * by content_id: the sequence is upload → get paths → create_post.
 */
export async function uploadPhoto(
  photo: PreparedPhoto,
  tenantId: string,
  personId: string,
): Promise<AttachmentInput> {
  const path = `${tenantId}/${personId}/${crypto.randomUUID()}.jpg`

  const { error } = await supabase.storage.from('post-media').upload(path, photo.file, {
    contentType: 'image/jpeg',
    // No upsert. A colliding uuid is a bug worth hearing about, not something
    // to paper over by overwriting somebody's photo.
    upsert: false,
  })
  if (error) throw error

  return {
    storage_path: path,
    mime_type: 'image/jpeg',
    byte_size: photo.file.size,
    width: photo.width,
    height: photo.height,
    alt_text: photo.altText.trim() || null,
    // True because preparePhoto re-encoded through a canvas. It is a claim, and
    // the column is named to say so.
    exif_stripped: true,
  }
}

/**
 * A displayable URL for a stored photo.
 *
 * The bucket is PRIVATE — a public bucket would make every site photograph
 * readable by URL with no session, which is the audience model bypassed by a
 * link. So each render needs a signed URL, and this is the only place that
 * knows it.
 *
 * One hour. Long enough that a reader scrolling a feed never sees an image
 * expire mid-session, short enough that a URL pasted into a chat is dead by the
 * time it is opened.
 */
export async function signedPhotoUrl(path: string): Promise<string | null> {
  // Cached for 50 of the URL's 60 minutes (141): the feed tile, the photo
  // viewer and the viewer's look-ahead all ask for the same photo, and three
  // signatures for one image is three round trips on a site connection. A
  // failure is not cached, so the next render tries again.
  const hit = signed.get(path)
  if (hit && Date.now() - hit.at < SIGNED_REUSE_MS) return hit.url
  const url = supabase.storage
    .from('post-media')
    .createSignedUrl(path, 3600)
    .then(({ data, error }) => (error ? null : data?.signedUrl ?? null))
  signed.set(path, { at: Date.now(), url })
  const out = await url.catch(() => null)
  if (out === null) signed.delete(path)
  return out
}

const SIGNED_REUSE_MS = 50 * 60 * 1000

/* ── 146: THE FULL-SIZE COPY OF AN ENTERED PHOTO ──────────────────────────────
   David, 10 Oct: when the contest box is ticked, keep a full-resolution copy
   for marketing. It goes to the private bucket `photo-originals` under the
   SAME path as the feed copy, so the database can key it by that path and its
   storage policy can ask the photo room about its own name.

   It is a canvas re-encode too, for the same reason the feed copy is: the
   picked file still carries GPS. The date taken and the camera are read from
   the EXIF first (lib/exif.ts) and sent separately; nothing else survives.

   16 megapixels at most. iOS Safari refuses a canvas larger than about 16.7
   MP, and a 48 MP phone photo would otherwise fail outright; 16 MP is still a
   4,900 x 3,260 image, about 16 in wide at 300 dpi. */
const MAX_ORIGINAL_PIXELS = 16_000_000
const ORIGINAL_QUALITY = 0.9

export async function prepareOriginal(
  file: Blob,
  crop: NormRect | null = null,
): Promise<{ blob: Blob; width: number; height: number }> {
  // FED-66: the SAME crop as the feed copy, or the tags (fractions of the
  // feed copy) point at the wrong place here, and whatever the poster cut
  // out comes back in a Gallery download.
  return encodeRegion(file, crop, { maxPixels: MAX_ORIGINAL_PIXELS, quality: ORIGINAL_QUALITY }).catch(() => {
    throw new Error('The full-size copy could not be made.')
  })
}

/**
 * Upload the full-size copy and record the date taken, camera and (149)
 * where it was taken. NEVER throws and never blocks the post: if the
 * full-size copy fails (memory on an old phone, a dropped connection) the
 * details are still recorded and the room shows the 2,000 px copy only.
 * Returns whether the full size landed.
 *
 * `keepFull`: the photo is licensed (150) — the full-size copy and the
 * location are for marketing, sales, training and other uses, never contests.
 * `camera` / `location`: the poster did not opt this photo out of them in the
 * opt-out dialog (150: separately; on by default, David 10 Oct). The
 * full-size copy's size is recorded either way — it is not about the poster.
 * Called AFTER set_photo_uses: set_photo_location checks the photo has a use.
 */
export async function keepOriginal(
  photo: PreparedPhoto,
  feedPath: string,
  opts: { keepFull: boolean; camera: boolean; location: boolean },
): Promise<boolean> {
  const sendLocation = opts.keepFull && opts.location
  // 319: a licensed photo whose location the poster opted out of records the
  // no, so a colleague cannot put it on the map later.
  if (opts.keepFull && !opts.location) {
    try {
      const { error } = await supabase.rpc('set_photo_location_choice', { p_storage_path: feedPath, p_keep: false })
      if (error) throw error
    } catch {
      /* Never a reason to fail a post. */
    }
  }
  if (!photo.source || (!opts.keepFull && !opts.camera)) return false
  const exif = opts.camera || sendLocation ? await readExif(photo.source) : null
  const cam = opts.camera ? exif : null
  let size: { width: number; height: number; bytes: number } | null = null
  if (opts.keepFull) {
    try {
      const orig = await prepareOriginal(photo.source, photo.crop ?? null)
      const { error } = await supabase.storage.from('photo-originals').upload(feedPath, orig.blob, {
        contentType: 'image/jpeg',
        upsert: false,
      })
      if (error) throw error
      size = { width: orig.width, height: orig.height, bytes: orig.blob.size }
    } catch {
      size = null
    }
  }
  const anyFact = cam && (cam.takenAtLocal || cam.make || cam.model)
  if (size || anyFact) {
    try {
      const { error } = await supabase.rpc('set_photo_source', {
        p_storage_path: feedPath,
        p_taken_at_local: cam?.takenAtLocal ?? null,
        p_taken_offset: cam?.takenOffset ?? null,
        p_camera_make: cam?.make ?? null,
        p_camera_model: cam?.model ?? null,
        p_original_width: size?.width ?? null,
        p_original_height: size?.height ?? null,
        p_original_bytes: size?.bytes ?? null,
      })
      if (error) throw error
    } catch {
      /* Metadata, never a reason to fail somebody's post. */
    }
  }
  if (sendLocation && exif && exif.latitude !== null && exif.longitude !== null) {
    try {
      const { error } = await supabase.rpc('set_photo_location', {
        p_storage_path: feedPath,
        p_latitude: exif.latitude,
        p_longitude: exif.longitude,
      })
      if (error) throw error
    } catch {
      /* Same: never a reason to fail a post. */
    }
  }
  return size !== null
}
const signed = new Map<string, { at: number; url: Promise<string | null> }>()

/**
 * 141. Whether one photo may be entered in the photo contest and used in
 * company communications and marketing. Only the uploader can call this for a
 * photo (the path's second segment); the server refuses anything else.
 *
 * Keyed by storage path, not by attachment, because edit_post replaces a
 * post's attachment rows on every edit while the file — and so the decision —
 * stays the same. No row means no: see migration 141's header.
 */
export async function setPhotoPermission(path: string, mayFeature: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_photo_permission', {
    p_storage_path: path,
    p_may_feature: mayFeature,
  })
  if (error) throw error
}

/**
 * 148. The modal's door: one photo, use by use, with an optional reason.
 * Uploader only (the server checks the path). No row still means no.
 */
export async function setPhotoUses(path: string, uses: PhotoUses, reason: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_photo_uses', {
    p_storage_path: path,
    p_contest: uses.contest,
    // 150: the licence is one switch; 148's three columns are written together.
    p_marketing: uses.licence,
    p_sales: uses.licence,
    p_other: uses.licence,
    p_reason: reason && reason.trim() ? reason.trim() : null,
  })
  if (error) throw error
}

/** Your own choices for these photos. A photo nobody decided on is absent. */
export async function myPhotoUses(paths: string[]): Promise<Map<string, { uses: PhotoUses; reason: string }>> {
  const out = new Map<string, { uses: PhotoUses; reason: string }>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('my_photo_uses', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, {
      // A row from 148's finer dialog counts as licensed if any licence column is on (as the server does).
      uses: { contest: r.contest === true, licence: r.marketing === true || r.sales === true || r.other === true },
      reason: (r.reason as string | null) ?? '',
    })
  }
  return out
}

/** 149. Whether the platform holds a location / camera and date for your own photos. Never the values. */
export async function myPhotoDetails(paths: string[]): Promise<Map<string, { location: boolean; camera: boolean }>> {
  const out = new Map<string, { location: boolean; camera: boolean }>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('my_photo_details', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, { location: r.has_location === true, camera: r.has_camera === true })
  }
  return out
}

/** 149. Take back where a photo was taken and its camera and date. */
export async function removePhotoDetails(path: string): Promise<void> {
  const { error } = await supabase.rpc('remove_photo_details', { p_storage_path: path })
  if (error) throw error
}

/** 150. Take back one kind of metadata: the camera and date, or the location. */
export async function removePhotoDetail(path: string, which: DetailKey): Promise<void> {
  const { error } = await supabase.rpc(which === 'camera' ? 'remove_photo_camera' : 'remove_photo_location', {
    p_storage_path: path,
  })
  if (error) throw error
}

/**
 * 142. What one entered photo earns, read from the tenant's star_earning_rule
 * (member-readable) so the composer says what the admin screen says. Null when
 * the rule is off, zero, or unreadable — the composer then shows no number
 * rather than a promise the server will not keep.
 */
export async function photoContestReward(
  tenantId: string,
): Promise<{ stars: number; cap: number } | null> {
  const { data, error } = await supabase
    .from('star_earning_rule')
    .select('stars, monthly_cap, is_enabled')
    .eq('tenant_id', tenantId)
    .eq('rule_key', 'photo.contest_entry')
    .maybeSingle()
  if (error || !data || !data.is_enabled || !(data.stars > 0)) return null
  return { stars: data.stars as number, cap: (data.monthly_cap as number | null) ?? 0 }
}

/** Your own decisions for these paths. A path with no decision is absent. */
export async function myPhotoPermissions(paths: string[]): Promise<Map<string, boolean>> {
  const out = new Map<string, boolean>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('my_photo_permissions', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as { storage_path: string; may_feature: boolean }[]) {
    out.set(r.storage_path, r.may_feature)
  }
  return out
}

/**
 * Pull #hashtags out of what somebody wrote.
 *
 * People type tags inline — "finished the causeway today #FraserDelta #CPT" —
 * rather than filling in a separate field, so the composer reads them out of
 * the text instead of asking twice.
 *
 * NORMALISATION IS NOT DONE HERE. app.normalise_tag() in the database is the
 * single definition of what makes two hashtags the same, and it folds accents;
 * doing it again in TypeScript would be a second implementation that disagrees
 * the first time somebody types #Peñuelas. This only finds the candidates and
 * hands the raw text over.
 *
 * The character class deliberately includes accented letters, so #Peñuelas is
 * matched as one tag rather than truncated at the ñ. \p{L} needs the u flag.
 */
export function extractTags(text: string): string[] {
  const found = text.match(/#[\p{L}\p{N}_-]{2,40}/gu) ?? []
  // De-duplicate case-insensitively so "#CPT ... #cpt" is offered once, while
  // leaving the exact spelling intact for the tag label.
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of found) {
    const key = raw.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(raw)
  }
  return out
}
